/**
 * @aiPurpose pure decision function for notification-sound playback
 *   (#1073). Called from the realtime `NOTIFICATIONS_UPDATED` handler
 *   in `RealtimeBootstrap`. Decides whether to play a sound for the
 *   carried event based on:
 *     - kind (only `updated` arms playback; `viewed` only resets the
 *       cursor),
 *     - active-context match (active drawer/tab suppresses sound and
 *       the auto-mark-viewed path takes over),
 *     - per-group preference enabled,
 *     - once-per-unread-cycle cursor.
 * @aiInvariant returns a discriminated outcome rather than a boolean
 *   so tests can assert each branch. The cursor mutation and the
 *   side-effecting `playNotificationSound` call both happen inside
 *   this function so callers do not need to coordinate them.
 */
import type { NotificationPreferences } from '@magimp/shared';
import { notificationTypeToPreferenceGroup } from '@magimp/shared';
import type { NotificationsUpdatedEvent } from '@magimp/shared-realtime';
import { hasPlayedFor, markPlayedFor, resetPlayedFor, soundCursorKey } from './playedCursor.js';
import { playNotificationSound, type AudioFactory } from './playSound.js';

export type SoundOutcome =
  | 'played'
  | 'suppressed-viewed'
  | 'suppressed-active'
  | 'suppressed-no-prefs'
  | 'suppressed-disabled'
  | 'suppressed-already';

export interface ProcessOptions {
  /** True when the active drawer/tab matches the event target. */
  isActiveMatch: boolean;
  /** Per-user preference row. `undefined` while the query is still loading. */
  prefs: NotificationPreferences | undefined;
  /** Test-only audio factory; production callers omit. */
  audioFactory?: AudioFactory;
}

// INVARIANT (#1073, Codex round-2 F-001): no client-side
//   self-notification check is performed here because the realtime
//   `NotificationsUpdatedEvent` payload is intentionally content-free
//   and carries NO actor identity (see the `@aiPii` block on the
//   shared schema). The "no self-notification" property is held one
//   level earlier, at the backend audience-narrowing step in
//   `apps/backend/src/modules/notifications/notifications.service.ts`
//   (`uniqueAudience(audienceUserIds, actorUserId)` strips the actor
//   before UPSERT + emit) — a self-targeted notification therefore
//   cannot reach this tab. Adding an actorUserId field to the wire
//   solely for a defensive frontend check would be a PII regression.
export function processNotificationSoundEvent(
  evt: NotificationsUpdatedEvent,
  opts: ProcessOptions,
): SoundOutcome {
  const key = soundCursorKey(evt.entityType, evt.entityId, evt.notificationType);
  if (evt.kind === 'viewed') {
    resetPlayedFor(key);
    return 'suppressed-viewed';
  }
  if (opts.isActiveMatch) {
    // INVARIANT (#1073): the active-context branch in the realtime
    // handler also fires a by-target mark-viewed POST. Suppressing
    // sound here AND letting the auto-view path clear the row keeps
    // "do not play sound for an aggregate that was immediately
    // cleared for the current context" from issue #1073.
    return 'suppressed-active';
  }
  if (!opts.prefs) return 'suppressed-no-prefs';
  const group = notificationTypeToPreferenceGroup[evt.notificationType];
  const groupPrefs = opts.prefs[group];
  if (!groupPrefs.enabled) return 'suppressed-disabled';
  if (hasPlayedFor(key)) return 'suppressed-already';
  markPlayedFor(key);
  void playNotificationSound(groupPrefs.soundKey, opts.audioFactory);
  return 'played';
}
